import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Mail, RefreshCw } from 'lucide-react';
import { dashboardApi } from '../services/api';

/** Pipeline funnel + per-mailbox deliverability + audit trail. */
const Pipeline = () => {
  const [data, setData] = useState(null);
  const [deliv, setDeliv] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [p, d, a] = await Promise.all([
        dashboardApi.getPipeline(),
        dashboardApi.getDeliverability(7).catch(() => ({ data: null })),
        dashboardApi.getAudit(30).catch(() => ({ data: { events: [] } }))
      ]);
      setData(p.data); setDeliv(d.data); setAudit(a.data.events || []);
      setError(null);
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };
  useEffect(() => { load(); }, []);

  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return <div className="p-6 text-gray-500">Loading pipeline…</div>;

  const c = data.counts;
  const stages = [
    ['Not contacted', c.not_contacted, 'bg-gray-100 text-gray-800'],
    ['Contacted', c.contacted, 'bg-yellow-100 text-yellow-800'],
    ['Replied', c.replied, 'bg-blue-100 text-blue-800'],
    ['Interested', c.interested, 'bg-emerald-100 text-emerald-800'],
    ['Booked', c.booked, 'bg-green-100 text-green-800']
  ];
  const lost = [
    ['Not interested', c.not_interested], ['Unsubscribed', c.unsubscribed], ['Bounced', c.bounced]
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline</h1>
          <p className="text-sm text-gray-500">
            {Number(c.total).toLocaleString()} influencers · {Number(data.eligible).toLocaleString()} eligible to push
          </p>
        </div>
        <button onClick={load} className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {stages.map(([label, value, cls]) => (
          <div key={label} className="bg-white rounded-lg shadow p-4">
            <p className="text-2xl font-bold text-gray-900">{Number(value || 0).toLocaleString()}</p>
            <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${cls}`}>{label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
        {lost.map(([label, value]) => (
          <div key={label} className="bg-white rounded-lg shadow p-4">
            <p className="text-xl font-semibold text-gray-700">{Number(value || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
        <Link to="/review" className="bg-white rounded-lg shadow p-4 hover:bg-gray-50">
          <p className="text-xl font-semibold text-yellow-700">{data.reviewQueue}</p>
          <p className="text-xs text-gray-500">Awaiting review →</p>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-3 border-b border-gray-200 flex items-center">
            <Mail className="w-4 h-4 mr-2 text-gray-400" />
            <h2 className="font-semibold text-gray-900">Mailbox deliverability</h2>
            {deliv?.ramp && (
              <span className="ml-auto text-xs text-gray-500">
                ramp: {deliv.ramp.perMailbox}/mailbox/day (week {deliv.ramp.weeksSinceStart})
              </span>
            )}
          </div>
          <div className="px-6 py-3">
            {!deliv?.health?.length && <p className="text-sm text-gray-500">No sending activity yet.</p>}
            {(deliv?.health || []).map(h => (
              <div key={h.mailbox} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <span className="text-sm text-gray-700">{h.mailbox}</span>
                <span className="text-xs text-gray-500">
                  sent {h.sent} · bounced {(h.bounceRate * 100).toFixed(1)}% · replied {(h.replyRate * 100).toFixed(1)}%
                  {h.overThreshold && <span className="ml-2 text-red-600 font-medium">over threshold</span>}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-3 border-b border-gray-200 flex items-center">
            <Activity className="w-4 h-4 mr-2 text-gray-400" />
            <h2 className="font-semibold text-gray-900">Recent changes</h2>
          </div>
          <div className="px-6 py-3 max-h-96 overflow-y-auto">
            {!audit.length && <p className="text-sm text-gray-500">Nothing changed yet.</p>}
            {audit.map((e, i) => (
              <div key={i} className="py-2 border-b border-gray-100 last:border-0 text-sm">
                <span className="text-gray-800 font-medium">{e.subject}</span>
                <span className="text-gray-500"> {e.old_value ?? '(unset)'} → {e.new_value ?? '(reset)'}</span>
                <div className="text-xs text-gray-400">
                  {e.kind} · {e.actor || 'unknown'} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pipeline;
